import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS, ROLE_LABELS, type RoleSlug } from '@lms/shared-types';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, apiDownload, listAllResource } from '@/lib/api';
import {
  userListConfirm,
  userListErrorToast,
  userListSuccessToast,
  type UserListAction,
} from '@/lib/user-feedback';
import { matchesSearch } from '@/lib/search-normalize';
import { extractUserRoleSlugs, normalizeUserRoles, primaryUserRoleSlug } from '@/lib/user-roles';

interface UserRow {
  id: string;
  name: string;
  fullName?: string;
  email: string;
  phone?: string;
  roles?: string[] | unknown;
  role?: string;
  status?: string;
  lastLoginAt?: string;
  avatarUrl?: string;
  trashed?: boolean;
}

const ROLE_BADGE: Record<string, 'primary' | 'secondary' | 'default'> = {
  developer: 'primary',
  'super-admin': 'primary',
  admin: 'primary',
  teacher: 'secondary',
  seremitt: 'secondary',
  student: 'default',
};

const FILTER_FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white py-3 px-4 text-base outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

const PER_PAGE = 10;

function roleLabel(slug: unknown): string {
  if (typeof slug !== 'string') return '—';
  return ROLE_LABELS[slug as RoleSlug] ?? slug;
}

function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? '';
  return (first + second).toUpperCase();
}

function primaryRole(row: UserRow) {
  return primaryUserRoleSlug(row);
}

function isUserTrashed(user: UserRow): boolean {
  return Boolean(user.trashed ?? user.status === 'deleted');
}

function isUserActive(user: UserRow): boolean {
  return user.status === 'active' && !isUserTrashed(user);
}

function relativeTimeEs(iso: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  if (abs < 60) return rtf.format(seconds, 'second');
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(seconds / 3600), 'hour');
  if (abs < 2592000) return rtf.format(Math.round(seconds / 86400), 'day');
  if (abs < 31536000) return rtf.format(Math.round(seconds / 2592000), 'month');
  return rtf.format(Math.round(seconds / 31536000), 'year');
}

function UserRowActions({ user, displayName }: { user: UserRow; displayName: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission, canManageUser } = usePermissions();
  const [confirmAction, setConfirmAction] = useState<UserListAction | null>(null);
  const trashed = isUserTrashed(user);
  const active = isUserActive(user);
  const manageable = canManageUser(extractUserRoleSlugs(user.roles));
  const canEdit = hasPermission(PERMISSIONS.USUARIOS_EDITAR) && manageable;
  const canDelete = hasPermission(PERMISSIONS.USUARIOS_ELIMINAR) && manageable;
  const statusAction: UserListAction = active ? 'suspend' : 'activate';

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['users'] });

  const toggleStatusMutation = useMutation({
    mutationFn: (action: 'suspend' | 'activate') =>
      api(`/users/${user.id}/${action === 'suspend' ? 'deactivate' : 'activate'}`, { method: 'PATCH' }),
    onSuccess: (_data, action) => {
      setConfirmAction(null);
      toast(userListSuccessToast(action));
      invalidate();
    },
    onError: (error, action) => {
      toast(userListErrorToast(action, error));
    },
  });

  const restoreMutation = useMutation({
    mutationFn: () => api(`/users/${user.id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      setConfirmAction(null);
      toast(userListSuccessToast('restore'));
      invalidate();
    },
    onError: (error) => {
      toast(userListErrorToast('restore', error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => api(`/users/${user.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setConfirmAction(null);
      toast(userListSuccessToast('delete'));
      invalidate();
    },
    onError: (error) => {
      toast(userListErrorToast('delete', error));
    },
  });

  const confirmCopy = confirmAction ? userListConfirm(confirmAction, displayName) : null;
  const confirmPending =
    toggleStatusMutation.isPending || restoreMutation.isPending || deleteMutation.isPending;

  function runConfirmedAction() {
    if (confirmAction === 'delete') deleteMutation.mutate();
    else if (confirmAction === 'restore') restoreMutation.mutate();
    else if (confirmAction === 'suspend' || confirmAction === 'activate') {
      toggleStatusMutation.mutate(confirmAction);
    }
  }

  return (
    <>
      <div className="flex items-center justify-end gap-1">
        {!trashed ? (
          <>
            {canEdit && (
              <Link
                to={`/usuarios/${user.id}/edit`}
                className="lms-gradebook__action"
                title="Editar"
                aria-label={`Editar ${displayName}`}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  edit
                </span>
              </Link>
            )}
            {canEdit && (
              <button
                type="button"
                className="lms-gradebook__action"
                title={active ? 'Suspender' : 'Activar'}
                aria-label={`${active ? 'Suspender' : 'Activar'} ${displayName}`}
                disabled={toggleStatusMutation.isPending}
                onClick={() => setConfirmAction(statusAction)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  block
                </span>
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                className="lms-gradebook__action"
                title="Eliminar"
                aria-label={`Eliminar ${displayName}`}
                onClick={() => setConfirmAction('delete')}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
              </button>
            )}
          </>
        ) : (
          canEdit && (
            <button
              type="button"
              className="lms-gradebook__action"
              title="Restaurar"
              aria-label={`Restaurar ${displayName}`}
              disabled={restoreMutation.isPending}
              onClick={() => setConfirmAction('restore')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                settings_backup_restore
              </span>
            </button>
          )
        )}
      </div>

      {confirmCopy && (
        <ConfirmModal
          open
          title={confirmCopy.title}
          description={confirmCopy.description}
          confirmLabel={confirmCopy.confirmLabel}
          variant={confirmCopy.variant}
          loading={confirmPending}
          onConfirm={runConfirmedAction}
          onClose={() => {
            if (!confirmPending) setConfirmAction(null);
          }}
        />
      )}
    </>
  );
}

export function UsersPage() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.USUARIOS_CREAR);
  const canExport = hasPermission(PERMISSIONS.USUARIOS_VER);
  const [searchQuery, setSearchQuery] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('');
  const [role, setRole] = useState('');
  const [trashed, setTrashed] = useState('without');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', trashed],
    queryFn: async () => {
      const res = await listAllResource<UserRow>('/users', { trashed });
      return {
        ...res,
        data: (res.data ?? []).map((user) => normalizeUserRoles(user)),
      };
    },
  });

  const allUsers = data?.data ?? [];

  const users = useMemo(() => {
    let rows = allUsers;
    if (searchQuery.trim()) {
      rows = rows.filter(
        (u) => matchesSearch(u.fullName ?? u.name, searchQuery) || matchesSearch(u.email, searchQuery),
      );
    }
    if (email.trim()) {
      rows = rows.filter((u) => matchesSearch(u.email, email));
    }
    if (phone.trim()) {
      const p = phone.trim();
      rows = rows.filter((u) => (u.phone ?? '').includes(p));
    }
    if (status) rows = rows.filter((u) => u.status === status);
    if (role) {
      rows = rows.filter(
        (u) => extractUserRoleSlugs(u.roles).includes(role) || primaryRole(u) === role,
      );
    }
    if (trashed === 'only') {
      rows = rows.filter((u) => isUserTrashed(u));
    } else if (trashed === 'without') {
      rows = rows.filter((u) => !isUserTrashed(u));
    }
    return rows;
  }, [allUsers, searchQuery, email, phone, status, role, trashed]);

  const filteredTotal = users.length;
  const totalPages = Math.max(1, Math.ceil(filteredTotal / PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const firstItem = filteredTotal === 0 ? 0 : (currentPage - 1) * PER_PAGE + 1;
  const lastItem = Math.min(currentPage * PER_PAGE, filteredTotal);

  const rows = useMemo(() => {
    const start = (currentPage - 1) * PER_PAGE;
    return users.slice(start, start + PER_PAGE);
  }, [users, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, email, phone, status, role, trashed]);

  const hasFilters = Boolean(
    searchQuery || email || phone || status || role || trashed !== 'without',
  );

  function clearFilters() {
    setSearchQuery('');
    setEmail('');
    setPhone('');
    setStatus('');
    setRole('');
    setTrashed('without');
  }

  async function handleExport() {
    try {
      setExporting(true);
      const qs = trashed !== 'without' ? `?trashed=${encodeURIComponent(trashed)}` : '';
      await apiDownload(`/users/export${qs}`);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="lms-admin-users lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=users" />
          <div>
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Gestión de Usuarios</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Administre las cuentas de usuario, roles y permisos del sistema.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 md:pl-14">
          {canExport && (
            <button
              type="button"
              onClick={() => void handleExport()}
              disabled={exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-dash-secondary px-5 py-2.5 text-sm font-medium text-dash-secondary transition-colors hover:bg-brand-50 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-xl" aria-hidden="true">
                download
              </span>
              {exporting ? 'Exportando…' : 'Exportar'}
            </button>
          )}
          {canCreate && (
            <Link
              to="/usuarios/importar"
              className="inline-flex items-center gap-2 rounded-lg border border-dash-secondary px-5 py-2.5 text-sm font-medium text-dash-secondary transition-colors hover:bg-brand-50"
            >
              <span className="material-symbols-outlined text-xl" aria-hidden="true">
                upload_file
              </span>
              Importar JSON
            </Link>
          )}
          {canCreate && (
            <Link
              to="/usuarios/create"
              className="inline-flex items-center gap-2 rounded-lg bg-dash-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-all hover:opacity-90"
            >
              <span className="material-symbols-outlined text-xl" aria-hidden="true">
                person_add
              </span>
              Agregar un nuevo usuario
            </Link>
          )}
        </div>
      </div>

      <div className="lms-credentials-glass-panel mb-8 rounded-xl p-6 shadow-sm">
        <div className="space-y-0">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative min-w-0 flex-1">
              <span
                className="material-symbols-outlined pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                aria-hidden="true"
              >
                search
              </span>
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar usuario..."
                aria-label="Buscar usuario"
                className={`${FILTER_FIELD} pl-12`}
              />
            </div>
            <button
              type="button"
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-dash-outline-variant px-6 py-3 text-sm font-medium text-dash-on-surface-variant transition-colors hover:bg-secondary-100 md:w-auto"
              onClick={() => setShowMoreFilters((v) => !v)}
            >
              <span className="material-symbols-outlined text-xl" aria-hidden="true">
                filter_list
              </span>
              Añadir filtro
            </button>
            <button
              type="button"
              className="px-4 py-3 text-sm font-medium text-dash-secondary transition-colors hover:underline"
              onClick={() => setShowMoreFilters((v) => !v)}
            >
              {showMoreFilters ? 'Mostrar menos...' : 'Mostrar más...'}
            </button>
          </div>

          {showMoreFilters && (
            <div className="mt-6 grid grid-cols-1 gap-4 border-t border-dash-outline-variant pt-6 md:grid-cols-2">
              <div className="space-y-2">
                <label htmlFor="user-email" className="text-sm font-medium text-dash-on-surface-variant">
                  Dirección de correo
                </label>
                <input
                  id="user-email"
                  type="search"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={FILTER_FIELD}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="user-phone" className="text-sm font-medium text-dash-on-surface-variant">
                  Teléfono
                </label>
                <input
                  id="user-phone"
                  type="search"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={FILTER_FIELD}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="user-status" className="text-sm font-medium text-dash-on-surface-variant">
                  Estado
                </label>
                <select
                  id="user-status"
                  name="status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className={FILTER_FIELD}
                >
                  <option value="">Todos</option>
                  <option value="active">Activo</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
              <div className="space-y-2">
                <label htmlFor="user-role" className="text-sm font-medium text-dash-on-surface-variant">
                  Rol
                </label>
                <select
                  id="user-role"
                  name="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className={FILTER_FIELD}
                >
                  <option value="">Todos</option>
                  {Object.entries(ROLE_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <label htmlFor="user-trashed" className="text-sm font-medium text-dash-on-surface-variant">
                  Eliminados
                </label>
                <select
                  id="user-trashed"
                  name="trashed"
                  value={trashed}
                  onChange={(e) => setTrashed(e.target.value)}
                  className={FILTER_FIELD}
                >
                  <option value="without">Sin eliminados</option>
                  <option value="with">Incluir eliminados</option>
                  <option value="only">Solo eliminados</option>
                </select>
              </div>
              {hasFilters && (
                <div className="md:col-span-2">
                  <button type="button" onClick={clearFilters} className="text-sm font-medium text-dash-secondary hover:underline">
                    Quitar filtros
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {error && (
        <p className="mb-4 text-sm text-red-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      <div className="lms-gradebook lms-gradebook--forum">
        <div className="lms-course-forum-card">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : allUsers.length === 0 || users.length === 0 ? (
          <div className="lms-gradebook__table-wrap">
            <div className="lms-gradebook__empty">
              <div className="lms-gradebook__empty-icon" aria-hidden="true">
                <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                  group_off
                </span>
              </div>
              <h3>{allUsers.length === 0 ? 'Sin usuarios' : 'Sin resultados'}</h3>
              <p>
                {allUsers.length === 0
                  ? 'No hay usuarios registrados.'
                  : 'No se encontraron usuarios con ese criterio.'}
              </p>
            </div>
          </div>
        ) : (
          <>
            <div className="lms-gradebook__table-wrap">
              <table className="lms-gradebook__table">
                <thead>
                  <tr>
                    <th className="lms-gradebook__th" scope="col">
                      Nombre completo
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Dirección de correo
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Teléfono
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Rol
                    </th>
                    <th className="lms-gradebook__th" scope="col">
                      Último acceso
                    </th>
                    <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((user) => {
                    const displayName = user.fullName ?? user.name;
                    const roleName = primaryRole(user);
                    const roleDisplay = roleName ? roleLabel(roleName) : null;
                    const trashedUser = isUserTrashed(user);

                    return (
                      <tr key={user.id}>
                        <th className="lms-gradebook__td" scope="row">
                          <div className="lms-gradebook__student">
                            {user.avatarUrl ? (
                              <img src={user.avatarUrl} alt="" className="lms-gradebook__avatar" />
                            ) : (
                              <div className="lms-gradebook__avatar" aria-hidden="true">
                                {initials(displayName)}
                              </div>
                            )}
                            <div className="flex min-w-0 flex-wrap items-center gap-2">
                              <Link to={`/usuarios/${user.id}`} className="lms-gradebook__name">
                                {displayName}
                              </Link>
                              {trashedUser ? <Badge variant="danger">Eliminado</Badge> : null}
                              {!trashedUser && user.status === 'inactive' ? (
                                <Badge variant="warning">Inactivo</Badge>
                              ) : null}
                            </div>
                          </div>
                        </th>
                        <td className="lms-gradebook__td">
                          <a href={`mailto:${user.email}`} className="lms-gradebook__mail">
                            {user.email}
                          </a>
                        </td>
                        <td className="lms-gradebook__td">
                          {user.phone ? user.phone : <span className="lms-gradebook__muted">—</span>}
                        </td>
                        <td className="lms-gradebook__td">
                          {roleDisplay ? (
                            <Badge variant={ROLE_BADGE[roleName ?? ''] ?? 'default'}>{roleDisplay}</Badge>
                          ) : (
                            <span className="lms-gradebook__muted">—</span>
                          )}
                        </td>
                        <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                          {user.lastLoginAt ? (
                            <span title={new Date(user.lastLoginAt).toLocaleString('es')}>
                              {relativeTimeEs(user.lastLoginAt)}
                            </span>
                          ) : (
                            <span className="lms-gradebook__muted">Nunca</span>
                          )}
                        </td>
                        <td className="lms-gradebook__td lms-gradebook__td--actions">
                          <UserRowActions user={user} displayName={displayName} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {filteredTotal > 0 && (
              <div className="lms-course-forum-card__footer">
                <span className="lms-course-forum-card__meta">
                  Mostrando <strong>{firstItem}</strong> a <strong>{lastItem}</strong> de{' '}
                  <strong>{filteredTotal}</strong> resultados
                </span>
                <div className="lms-course-forum-card__pager">
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página anterior"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_left
                    </span>
                  </button>
                  <span className="lms-course-forum-card__pager-current">{currentPage}</span>
                  <button
                    type="button"
                    className="lms-course-forum-card__pager-btn"
                    aria-label="Página siguiente"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
}
